import express, { Router, Request, Response, NextFunction } from 'express';
import validate from '@/shared/utils/middlewares/validate.middleware';
import { commonValidation } from './property.validation';

// Controllers
import * as categoryController from '@/modules/master/property/category/category.controller';
import * as subCategoryController from '@/modules/master/property/subCategory/subCategory.controller';
import * as propertyUsageController from '@/modules/master/property/propertyUsage/propertyUsage.controller';
import * as propertySizeController from '@/modules/master/property/propertySize/propertySize.controller';
import * as configurationController from '@/modules/master/property/configuration/configuration.controller';

import userAuthAndCheckPermissions from '@/modules/auth/auth.user.middleware';
import { setAuditFields } from '@/shared/middleware/setAuditFields';
import { AuditMode } from '@/shared/constants/enum.constant';

const router: Router = express.Router();

const controllersMap: Record<string, unknown> = {
  category: categoryController,
  subCategory: subCategoryController,
  propertyUsage: propertyUsageController,
  propertySize: propertySizeController,
  configuration: configurationController,
};

function dynamicValidation(action: 'get' | 'create' | 'update' | 'delete') {
  return (req: Request, res: Response, next: NextFunction) => {
    const { type } = req.params;

    const typeValidation = (commonValidation as unknown)[type];
    if (!typeValidation || !typeValidation[action])
      return next(
        new Error(
          `Validation schema not found for type: ${type} and action: ${action}`,
        ),
      );

    return validate(typeValidation[action])(req, res, next);
  };
}

function controllerHandler(
  action: 'create' | 'list' | 'get' | 'update' | 'remove',
) {
  return async (req: Request, res: Response, next: NextFunction) => {
    const { type } = req.params;
    const controller = controllersMap[type];
    if (!controller || typeof controller[action] !== 'function')
      return next(new Error(`Handler for ${action} not found in ${type}`));

    return controller[action](req, res, next);
  };
}

router
  .route('/:type')
  .post(
    userAuthAndCheckPermissions(),
    dynamicValidation('create'),
    setAuditFields({ mode: AuditMode.CREATE }),
    controllerHandler('create'),
  )
  .get(
    userAuthAndCheckPermissions(),
    dynamicValidation('get'),
    controllerHandler('list'),
  );

router
  .route('/:type/:id')
  .get(
    userAuthAndCheckPermissions(),
    dynamicValidation('get'),
    controllerHandler('get'),
  )
  .patch(
    userAuthAndCheckPermissions(),
    dynamicValidation('update'),
    setAuditFields({ mode: AuditMode.UPDATE }),
    controllerHandler('update'),
  )
  .delete(
    userAuthAndCheckPermissions(),
    dynamicValidation('delete'),
    controllerHandler('remove'),
  );

export default router;
